import { computed } from 'mobx';

import { ContextService } from 'src/core/services/context.service';
import { EntityModel } from 'src/core/services/data/models';
import { DataService, property } from 'src/core/services/data.service';
import { BookemZulu as Model, OData, $data } from 'src/core/services/data/JayDataContext';
import { FormatService } from 'src/core/services/format.service';

export class PolicyRuleDataService extends DataService<typeof Model.Bookings.Logic.ViewModels.PolicyRuleItem, Model.Bookings.Logic.ViewModels.PolicyRuleItem, PolicyRuleItem> {
	constructor(contextService: ContextService, entitySet: (context: OData.Container) => $data.EntitySet<typeof Model.Bookings.Logic.ViewModels.PolicyRuleItem, Model.Bookings.Logic.ViewModels.PolicyRuleItem>, private formatService: FormatService) {
		super(contextService, entitySet, false);
	}

	protected map(entity: Model.Bookings.Logic.ViewModels.PolicyRuleItem): PolicyRuleItem {
		return new PolicyRuleItemModel(entity, this.formatService);
	}

	static make(ctx: ContextService, formatService: FormatService): PolicyRuleDataService {
		return new PolicyRuleDataService(ctx, ctx => ctx.PolicyRules, formatService);
	}

	private id = -1;
	add(owner: PolicyRuleOwner): Promise<PolicyRuleItem> {
		const entity = new Model.Bookings.Logic.ViewModels.PolicyRuleItem();

		if (owner.policy != null) {
			entity.quoteProductPolicy = owner.policy.entity as any;
			if (owner.policyId > 0) {
				entity.quoteProductPolicyId = owner.policyId;
			}
		}

		if (owner.quotePolicy != null) {
			entity.quotePolicy = owner.quotePolicy.entity as any;
			if (owner.quotePolicyId > 0) {
				entity.quotePolicyId = owner.quotePolicyId;
			}
		}

		if (owner.sellMarginPolicy != null) {
			entity.sellMarginPolicy = owner.sellMarginPolicy.entity as any;
			if (owner.sellMarginPolicyId > 0) {
				entity.sellMarginPolicyId = owner.sellMarginPolicyId;
			}
		}
		if (owner.quoteProductVisibilityPolicy != null) {
			entity.quoteProductVisibilityPolicy = owner.quoteProductVisibilityPolicy.entity as any;
			if (owner.quoteProductVisibilityPolicyId > 0) {
				entity.quoteProductVisibilityPolicyId = owner.quoteProductVisibilityPolicyId;
			}
		}
		entity.id = this.id--;

		return this.create(() => entity);
	}
}

export class PolicyRuleItemModel extends EntityModel<Model.Bookings.Logic.ViewModels.PolicyRuleItem, PolicyRuleItem> implements PolicyRuleItem {
	@property<PolicyRuleItemModel>(o => o.entity.id, (o, value) => o.entity.id = value)
	public id: number;

	@property<PolicyRuleItemModel>(o => o.entity.quoteProductPolicyId, (o, value) => o.entity.quoteProductPolicyId = value)
	public quoteProductPolicyId: number;

	@property<any>(o => o.entity.quoteProductPolicy, (o, value) => o.entity.quoteProductPolicy = value)
	public quoteProductPolicy: any;

	@property<PolicyRuleItemModel>(o => o.entity.quotePolicyId, (o, value) => o.entity.quotePolicyId = value)
	public quotePolicyId: number;

	@property<any>(o => o.entity.quotePolicy, (o, value) => o.entity.quotePolicy = value)
	public quotePolicy: any;

	@property<PolicyRuleItemModel>(o => o.entity.sellMarginPolicyId, (o, value) => o.entity.sellMarginPolicyId = value)
	public sellMarginPolicyId: number;

	@property<any>(o => o.entity.sellMarginPolicy, (o, value) => o.entity.sellMarginPolicy = value)
	public sellMarginPolicy: any;

	@property<PolicyRuleItemModel>(o => o.entity.quoteProductVisibilityPolicyId, (o, value) => o.entity.quoteProductVisibilityPolicyId = value)
	public quoteProductVisibilityPolicyId: number;

	@property<any>(o => o.entity.quoteProductVisibilityPolicy, (o, value) => o.entity.quoteProductVisibilityPolicy = value)
	public quoteProductVisibilityPolicy: any;

	@property<PolicyRuleItemModel>(o => o.entity.brandId, (o, value) => o.entity.brandId = value)
	public brandId: number;

	@property<PolicyRuleItemModel>(o => o.entity.brandName, (o, value) => o.entity.brandName = value)
	public brandName: string;

	@property<PolicyRuleItemModel>(o => o.entity.marketId, (o, value) => o.entity.marketId = value)
	public marketId: number;

	@property<PolicyRuleItemModel>(o => o.entity.marketName, (o, value) => o.entity.marketName = value)
	public marketName: string;

	@property<PolicyRuleItemModel>(o => o.entity.placeId, (o, value) => o.entity.placeId = value)
	public placeId: number;

	@property<PolicyRuleItemModel>(o => o.entity.placeDisplayName, (o, value) => o.entity.placeDisplayName = value)
	public placeDisplayName: string;

	@property<PolicyRuleItemModel>(o => o.entity.productCategoryId, (o, value) => o.entity.productCategoryId = value)
	public productCategoryId: number;

	@property<PolicyRuleItemModel>(o => o.entity.productCategoryDisplayName, (o, value) => o.entity.productCategoryDisplayName = value)
	public productCategoryDisplayName: string;

	@property<PolicyRuleItemModel>(o => o.entity.productId, (o, value) => o.entity.productId = value)
	public productId: number;

	@property<PolicyRuleItemModel>(o => o.entity.productDisplayName, (o, value) => o.entity.productDisplayName = value)
	public productDisplayName: string;

	@property<PolicyRuleItemModel>(o => o.entity.supplierId, (o, value) => o.entity.supplierId = value)
	public supplierId: number;

	@property<PolicyRuleItemModel>(o => o.entity.supplierTitle, (o, value) => o.entity.supplierTitle = value)
	public supplierTitle: string;

	@property<PolicyRuleItemModel>(o => o.entity.regionId, (o, value) => o.entity.regionId = value)
	public regionId: number;

	@property<PolicyRuleItemModel>(o => o.entity.regionDisplayName, (o, value) => o.entity.regionDisplayName = value)
	public regionDisplayName: string;

	@property<PolicyRuleItemModel>(o => o.entity.productType, (o, value) => o.entity.productType = value)
	public productType: number;

	@computed
	public get productTypeText(): string {
		switch (this.entity.productType) {
			case 0:
				return 'General';
			case 1:
				return 'Accomodation';
			case 2:
				return 'Car rent';
			case 3:
				return 'Transfer';
			case 4:
				return 'Flight';
			case 5:
				return 'Room';
			case 6:
				return 'Supplement/Discount';
			case 7:
				return 'Sales Adjustment';
			case 8:
				return 'Accounting Adjustment';
			case 9:
				return 'Tour';
			case 10:
				return 'Package';
		}
		return null;
	}

	@property<PolicyRuleItemModel>(o => o.entity.bookingDateFrom, (o, value) => o.entity.bookingDateFrom = value)
	public bookingDateFrom: Date;

	@computed
	public get bookingDateFromText(): string {
		return this.bookingDateFrom == null ? null : this.formatService.toString('short-date', this.bookingDateFrom, "utc");
	}

	@property<PolicyRuleItemModel>(o => o.entity.bookingDateTo, (o, value) => o.entity.bookingDateTo = value)
	public bookingDateTo: Date;

	@computed
	public get bookingDateToText(): string {
		return this.bookingDateTo == null ? null : this.formatService.toString('short-date', this.bookingDateTo, "utc");
	}

	@property<PolicyRuleItemModel>(o => o.entity.serviceDateFrom, (o, value) => o.entity.serviceDateFrom = value)
	public serviceDateFrom: Date;

	@computed
	public get serviceDateFromText(): string {
		return this.serviceDateFrom == null ? null : this.formatService.toString('short-date', this.serviceDateFrom, "utc");
	}

	@property<PolicyRuleItemModel>(o => o.entity.serviceDateTo, (o, value) => o.entity.serviceDateTo = value)
	public serviceDateTo: Date;
	@property<PolicyRuleItemModel>(o => o.entity.productProviderId, (o, value) => o.entity.productProviderId = value)
	productProviderId?: number
	@property<PolicyRuleItemModel>(o => o.entity.productProviderName, (o, value) => o.entity.productProviderName = value)
	productProviderName?: string

	@computed
	public get serviceDateToText(): string {
		return this.serviceDateTo == null ? null : this.formatService.toString('short-date', this.serviceDateTo, "utc");
	}
	validate(validateChildren: boolean, property?: string) {
		let errors: ValidationError[] = [];
		
			if (this.bookingDateFrom == undefined &&
				this.bookingDateTo == undefined &&
				this.serviceDateFrom == undefined &&
				this.serviceDateTo == undefined &&
				this.brandId == undefined &&
				this.marketId == undefined &&
				this.regionId == undefined &&
				this.placeId == undefined &&
				this.productCategoryId == undefined &&
				this.productId == undefined &&
				this.productType == undefined &&
				this.supplierId == undefined &&
				this.productType == undefined
			)
				errors.push({
					property: "Rules",
					text: "At least one property of the rule must have a value to save it",
				});
		return new Promise<ValidationError[]>((resolve, reject) => {
			resolve(errors);
		})
		 
	}
	constructor(entity: Model.Bookings.Logic.ViewModels.PolicyRuleItem, private formatService: FormatService) {
		super(entity);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/policy-rules/policy-rule-form.data.ts